import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Fragment } from 'react';
import { useFormContext } from 'react-hook-form';
import { PaymentFormValues } from 'src/pages/payment-msi/PaymentMsiV2/PaymentMsiV2';
import { AdditionalInsuredSection } from 'src/pages/payment-msi/PaymentMsiV2/components/AdditionalInsuredSection';
import { BillingAddressSection } from 'src/pages/payment-msi/PaymentMsiV2/components/BillingAddressSection';
import { InterestedPartySection } from 'src/pages/payment-msi/PaymentMsiV2/components/InterestedPartySection';
import { LegalSection } from 'src/pages/payment-msi/PaymentMsiV2/components/LegalSection';
import { PaymentOptionSection } from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentOptionSection';
import { PaymentPlansSection } from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentPlansSection';
import { PaymentSectionDivider } from 'src/pages/payment-msi/PaymentMsiV2/components/PaymentSection';
import { PaymentSectionId } from 'src/pages/payment-msi/PaymentMsiV2/constants';
import { usePaymentLayoutContext } from 'src/pages/payment-msi/PaymentMsiV2/providers';
import {
  BillingAddress,
  InterestedParty,
} from 'src/pages/payment-msi/PaymentMsiV2/types';
import {
  PaymentPlanOption,
  purgeAdditionalInsuredsForForm,
  purgeInterestedPartyForForm,
  scrollToFirstError,
  validateAdditionalInsured,
  validateInterestedParty,
} from 'src/pages/payment-msi/PaymentMsiV2/utils';
import { AppTheme } from 'src/themes/muiTheme';

interface PaymentFormBodyProps {
  landlordData: InterestedParty | null;
  buildingAddress: BillingAddress;
  isSubmitDisabled: boolean;
  paymentPlanOptions: PaymentPlanOption[];
  isLoading: boolean;
}

function MobilePaymentFormBody({
  buildingAddress,
  landlordData,
  isSubmitDisabled,
  paymentPlanOptions,
  isLoading,
}: PaymentFormBodyProps) {
  const { mobileBodyPart, updateMobileBodyPart } = usePaymentLayoutContext();
  const { getValues, setError } = useFormContext<PaymentFormValues>();

  function onContinue() {
    const formValues = getValues();
    const additionalInsuredValues = purgeAdditionalInsuredsForForm(
      formValues.additionalInsured,
    );
    const interestedPartyValue = purgeInterestedPartyForForm(
      formValues.interestedParty,
    );

    const additionalInsuredErrors = validateAdditionalInsured(
      additionalInsuredValues,
    );
    const interestedPartyErrors = validateInterestedParty(interestedPartyValue);
    const formErrors = [...additionalInsuredErrors, ...interestedPartyErrors];

    if (formErrors.length > 0) {
      for (const formError of formErrors) {
        setError(formError.fieldName as keyof PaymentFormValues, {
          message: formError.message,
        });
      }

      scrollToFirstError(formErrors);
      return;
    }

    updateMobileBodyPart('checkout');

    const paymentPlansSection = document.getElementById(
      PaymentSectionId.PaymentPlans,
    );

    if (!paymentPlansSection) {
      return;
    }

    const topOffset = paymentPlansSection.offsetTop - window.innerHeight * 0.5;
    window.scrollBy({
      top: topOffset,
      behavior: 'smooth',
    });
  }

  return (
    <Fragment>
      <PaymentPlansSection paymentPlanOptions={paymentPlanOptions} />
      <Box display={mobileBodyPart === 'add-parties' ? 'block' : 'none'}>
        <PaymentSectionDivider />
        <InterestedPartySection landlordData={landlordData} />
        <PaymentSectionDivider />
        <AdditionalInsuredSection />
        <Button fullWidth={true} variant="contained" onClick={onContinue}>
          Continue
        </Button>
      </Box>
      <Box display={mobileBodyPart === 'checkout' ? 'block' : 'none'}>
        <PaymentSectionDivider />
        <PaymentOptionSection />
        <PaymentSectionDivider />
        <BillingAddressSection buildingAddress={buildingAddress} />
        <PaymentSectionDivider />
        <LegalSection paymentPlanOptions={paymentPlanOptions} />
        <Button
          fullWidth={true}
          disabled={isSubmitDisabled}
          variant="contained"
          type="submit"
          startIcon={
            isLoading ? (
              <div style={{ height: '20px', marginRight: '5px' }}>
                <CircularProgress color="inherit" size="17px" />
              </div>
            ) : null
          }
        >
          Pay Now
        </Button>
      </Box>
    </Fragment>
  );
}

function DesktopPaymentFormBody({
  buildingAddress,
  landlordData,
  isSubmitDisabled,
  paymentPlanOptions,
  isLoading,
}: PaymentFormBodyProps) {
  return (
    <Fragment>
      <PaymentPlansSection paymentPlanOptions={paymentPlanOptions} />
      <PaymentSectionDivider />
      <InterestedPartySection landlordData={landlordData} />
      <PaymentSectionDivider />
      <AdditionalInsuredSection />
      <PaymentSectionDivider />
      <PaymentOptionSection />
      <PaymentSectionDivider />
      <BillingAddressSection buildingAddress={buildingAddress} />
      <PaymentSectionDivider />
      <LegalSection paymentPlanOptions={paymentPlanOptions} />
      <Button
        startIcon={
          isLoading ? (
            <div style={{ height: '20px', marginRight: '5px' }}>
              <CircularProgress color="inherit" size="17px" />
            </div>
          ) : null
        }
        disabled={isSubmitDisabled}
        variant="contained"
        type="submit"
      >
        Pay Now
      </Button>
    </Fragment>
  );
}

export const PaymentFormBody = (props: PaymentFormBodyProps) => {
  const isMobile = useMediaQuery<AppTheme>((theme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    return theme.breakpoints.down(mobileMaxSize);
  });

  return isMobile ? (
    <MobilePaymentFormBody {...props} />
  ) : (
    <DesktopPaymentFormBody {...props} />
  );
};
